import React, { useState } from 'react';
import { generatePassword, copyToClipboard } from '../../utils/passwordGenerator';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  portalUser: {
    id: number;
    user_email: string;
  };
  customerId: number;
  onSuccess: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  portalUser,
  customerId,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetPassword, setResetPassword] = useState('');

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    setShowPassword(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/customers/${customerId}/portal_users/${portalUser.id}/reset_password/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            password,
            send_email: sendEmail,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setResetPassword(password);
      setSuccess(true);
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = async () => {
    const text = `Password Reset for ${portalUser.user_email}

New Password: ${resetPassword}`;

    const copied = await copyToClipboard(text);
    if (copied) {
      alert('Password copied to clipboard!');
    }
  };

  const handleClose = () => {
    setPassword('');
    setShowPassword(false);
    setSendEmail(false);
    setError('');
    setSuccess(false);
    setResetPassword('');
    onClose();
  };

  if (!isOpen) return null;

  if (success && resetPassword) {
    return (
      <div style={styles.overlay} onClick={handleClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>✅ Password Reset!</h2>
            <button onClick={handleClose} style={styles.closeButton}>×</button>
          </div>

          <div style={styles.body}>
            <p style={styles.successMessage}>
              Password has been reset for <strong>{portalUser.user_email}</strong>
            </p>

            <div style={styles.credentialsBox}>
              <div style={styles.credentialRow}>
                <strong>New Password:</strong> {resetPassword}
              </div>
            </div>

            <p style={styles.note}>
              Make sure to save this password securely. The user will need it to log in.
            </p>

            <div style={styles.actions}>
              <button onClick={handleCopyPassword} style={styles.copyButton}>
                Copy Password
              </button>
              <button onClick={handleClose} style={styles.doneButton}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Reset Password</h2>
          <button onClick={handleClose} style={styles.closeButton}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.body}>
          <div style={styles.userInfo}>
            Reset password for: <strong>{portalUser.user_email}</strong>
          </div>

          {error && (
            <div style={styles.errorBox}>
              {error}
            </div>
          )}

          <div style={styles.formGroup}>
            <label htmlFor="password" style={styles.label}>
              New Password <span style={styles.required}>*</span>
            </label>
            <div style={styles.passwordGroup}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{...styles.input, flex: 1}}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.iconButton}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁' : '👁‍🗨'}
              </button>
              <button
                type="button"
                onClick={handleGeneratePassword}
                style={styles.generateButton}
              >
                Generate Random
              </button>
            </div>
            {password && (
              <div style={styles.generatedPassword}>
                Generated: <strong>{password}</strong>
                <button
                  type="button"
                  onClick={() => copyToClipboard(password)}
                  style={styles.copySmallButton}
                >
                  Copy
                </button>
              </div>
            )}
          </div>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <span>Send password reset email to user</span>
          </label>

          <div style={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitButton,
                ...(loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#6b7280',
    lineHeight: 1,
  },
  body: {
    padding: '1.5rem',
  },
  userInfo: {
    padding: '0.75rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem',
  },
  required: {
    color: '#dc2626',
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  passwordGroup: {
    display: 'flex',
    gap: '0.5rem',
  },
  iconButton: {
    padding: '0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'white',
    fontSize: '1rem',
  },
  generateButton: {
    padding: '0.625rem 1rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  generatedPassword: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f0fdf4',
    borderRadius: '4px',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  copySmallButton: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '0.75rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
    cursor: 'pointer',
    marginBottom: '1.5rem',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1e3a8a',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  successMessage: {
    fontSize: '1rem',
    color: '#374151',
    marginBottom: '1.5rem',
  },
  credentialsBox: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  credentialRow: {
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
    wordBreak: 'break-all',
  },
  note: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginBottom: '1.5rem',
  },
  copyButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  doneButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1e3a8a',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
};

export default ResetPasswordModal;
